// @givmo/board — the module's identity and its console addresses (CBG-MOD1; the plug contract's
// package exports, the Books shape). Both class consoles mount the module at the same path, and
// every page below lives under it.

export const BOARD_SLUG = 'board';
export const BOARD_ROUTE = '/board';

/**
 * The Board module's own read string. Every officer read operation's decorator names it, so a
 * sidebar row that opens a Board page requires it (the backend half of CBG-MOD1).
 */
export const BOARD_READ_PERMISSION = 'board:read';

/**
 * The module's pages as paths RELATIVE to its mount — the words the package's own links use
 * (`board-routing.tsx` prefixes the host's base path).
 */
export const BOARD_PAGES = {
  meetings: '/meetings',
  consents: '/consents',
  conflicts: '/conflicts',
  /** CBG-YR2-WEB1: one person's answers for a year (`?view=changes` for the changes from last year). */
  coiResponse: '/conflicts/annual/$cycleId/responses/$recordId',
  minuteBook: '/records',
  governance: '/governance',
  /**
   * CBG-YR5-VIEW: the Form 990 page, after Governance under Records (D-CBG-9 amended, CBG-YR5's Q2) —
   * one tax year of Part VI, `?tax_year_ends_on=` naming it, and with no fiscal year end recorded
   * `?year_end_month=&year_end_day=` the day chosen (frames p1, p2, p3, p9).
   */
  form990: '/form-990',
  /**
   * CBG-YR5-DIST and CBG-YR5-REVIEW: one copy of the return provided to the board, its receipts and
   * the board's review (frame p7), beneath the Form 990 page, so its row stays lit there.
   */
  returnCopy: '/form-990/returns/$recordId',
  /**
   * CBG-YR5-SCHO: the Schedule O draft (frame p8), beneath the Form 990 page so its row stays lit,
   * carrying the Form 990 page's address (`?tax_year_ends_on=`, and a chosen `?year_end_month=&
   * year_end_day=`). Reached from line 11b and from line 8b's no-committee note.
   */
  scheduleO: '/form-990/schedule-o',
  members: '/roster',
  /** CBG-YR6-REG: the board's committees, and one committee (frames c1, c2). */
  committees: '/committees',
  committee: '/committees/$committeeId',
  /**
   * CBG-YR4-ASSESS-WEB: the board's self-assessment, `?assessment=<id>` naming one (frames s1, s3). It
   * shows as "Self-assessment" (CBG-UX8-MENU; D-CBG-35 (1)); its address and ids keep the page's first
   * word, "evaluations", because they are the testid and link contract.
   */
  evaluations: '/evaluations',
  importBatches: '/import-batches',
  /**
   * CBG-AI5-PROPOSE: a batch of past records the organization's connected AI proposed, and its one
   * confirmation (frames b1–b8), beneath the import workspace so the Minute book row stays lit. The
   * connector's answers name this address (`review_url`, backend `board-ai-proposals.md` §3.5, from
   * `BATCH_PAGE` in `app/application/board_proposal_service.py`): change both sides in one round,
   * never one.
   */
  proposedBatch: '/import-batches/proposed/$batchId',
  /**
   * CBG-AI5-INBOX-WEB: the upload page, a folder a person uploads for their AI (frames u2–u4), beneath
   * the import workspace so the Minute book row stays lit.
   */
  importUploads: '/import-batches/uploads',
  /** EMC-WEB4: the Board Asks, and one ask (frames 23 and 32). */
  asks: '/asks',
  ask: '/asks/$askId',
} as const;

/**
 * The director's pages, relative to the mount. `redeem` is where every board invitation lands
 * (CBG-FIX14's one link format, `director/director-access-link.ts`), and `packet` where a joined
 * seat's packet notice does (with `?org=`); the backend's mails spell the host console's whole
 * address for both, pinned against these from both sides. `signIn` is the director door, where the
 * director family's guard sends a signed-out visitor. Every board page carries `?org=<org_ref>`;
 * `signInSecurity` is the account's own page (Account Security's card), about no one board.
 */
export const BOARD_DIRECTOR_PAGES = {
  home: '/director',
  signInSecurity: '/director/sign-in-security',
  redeem: '/director/redeem',
  /** CBG-SEC2-L4-MOVE: a director moves their own seat to another Givmo account (D-CBG-23 flow (b)). */
  move: '/director/move',
  signIn: '/director/sign-in',
  meetings: '/director/meetings',
  meeting: '/director/meetings/$meetingId',
  consents: '/director/consents',
  consent: '/director/consents/$consentId',
  disclosures: '/director/disclosures',
  /**
   * CBG-YR2-WEB2: the year's own questionnaire, `?org=<org_ref>&cycle=<cycle_id>` (`&view=changes` for
   * the person's changes). The backend's request mail spells this console's whole address
   * (`BOARD_DIRECTOR_ANNUAL_DISCLOSURE_PATH` in `app/domain/board_director_links.py`): change both
   * sides in one round, never one.
   */
  annualDisclosure: '/director/disclosures/annual',
  packet: '/director/packets/$packetId',
  records: '/director/records',
  record: '/director/records/$recordId',
  /** CBG-YR4-START: the member's own getting-started list, `?org=<org_ref>`. */
  gettingStarted: '/director/getting-started',
  /** CBG-YR4-ABOUT: About the board, assembled from the board's own records, `?org=<org_ref>`. */
  about: '/director/about',
  /**
   * CBG-UX10 (D-CBG-40): Board members, the officer page's name for who serves and what each does,
   * `?org=<org_ref>`. About the board's read; About itself stays the new member checklist's page.
   */
  members: '/director/members',
  /** CBG-UX10: the committees the seat sits on, and when each meets next, `?org=<org_ref>`. */
  committees: '/director/committees',
  /**
   * CBG-YR4-ASSESS-WEB: the board's self-assessment as the person answers it (frame s2),
   * `?org=<org_ref>&assessment=<id>`. The backend's ask and reminder mails spell this console's whole
   * address (`board_director_self_assessment_url` in `app/domain/board_director_links.py`): change
   * both sides in one round, never one.
   */
  selfAssessment: '/director/self-assessment',
  /**
   * CBG-YR5-DIST: a copy of the return provided to the board, as the person reads it (frame p6),
   * `?org=<org_ref>`. The backend's mail spells this console's whole address
   * (`BOARD_DIRECTOR_RETURN_PATH` in `app/domain/board_director_links.py`): change both sides in one
   * round, never one.
   */
  returnCopy: '/director/returns/$recordId',
  /** EMC-WEB4: a Board Ask sent to the seat, `?org=` (the ask's mail spells it, `BOARD_DIRECTOR_ASK_PATH`). */
  ask: '/director/asks/$askId',
} as const;

/**
 * Where a legal hold's report lives, relative to the mount: `/legal-holds/$holdId` sits beneath it.
 * Not a row (no officer screen lists the holds since D-CBG-53; the report keeps its address), so the
 * report keeps the Minute book row lit (`board-nav.ts`).
 */
export const BOARD_LEGAL_HOLDS = '/legal-holds';

/**
 * The import workspace's console addresses. Declared HERE, beside the pages, because the module owns
 * its addresses — and because a host's own copy rail (a class console's bans the word the workspace's
 * path carries) reads every string literal in the host, where a route path is indistinguishable from
 * copy. The host routes reference these constants rather than spelling the paths.
 */
export const BOARD_IMPORT_ROUTES = {
  batches: '/board/import-batches',
  batch: '/board/import-batches/$batchId',
  // CBG-AI5-PROPOSE: a proposed batch's page (`BOARD_PAGES.proposedBatch`).
  proposedBatch: '/board/import-batches/proposed/$batchId',
  // CBG-AI5-INBOX-WEB: the upload page (`BOARD_PAGES.importUploads`).
  uploads: '/board/import-batches/uploads',
} as const;

/**
 * A page's console address: the module's mount followed by the page. Meetings too — the mount itself
 * is Board home, which a host replaces with Meetings, so the Meetings row is an ordinary
 * segment-prefix row that stays lit on every meeting's own pages (the shell's family root matches
 * exactly, which is what left a meeting page with no row lit — Jonathan, 2026-09-28).
 */
export function boardConsolePath(page: string): string {
  return `${BOARD_ROUTE}${page}`;
}

/**
 * The Governance page's two parts, as the words its address carries (`?tab=`), deep-linkable — the
 * part labels' own words (the Profile page's `?tab=` precedent).
 */
export const GOVERNANCE_TABS = {
  documents: 'documents',
  meetingRules: 'meeting-rules',
  // CBG-YR3-TERMS: the board's own term rules and classes (frame t2), between the meeting rules and
  // the officers.
  terms: 'terms',
  // CBG-YR3-ROLES layer 2: the board's own officer titles (`l2-officers`).
  officers: 'officers',
} as const;
export type GovernanceTab = (typeof GOVERNANCE_TABS)[keyof typeof GOVERNANCE_TABS];

/**
 * The Conflicts of interest page's two parts (CBG-YR2-WEB1), as the words its address carries
 * (`?tab=`), deep-linkable like Governance's. An address with no part opens the Register.
 */
export const CONFLICTS_TABS = {
  register: 'register',
  annual: 'annual',
} as const;
export type ConflictsTab = (typeof CONFLICTS_TABS)[keyof typeof CONFLICTS_TABS];

/**
 * The Form 990 page's two parts, as the words its address carries (`?tab=`), deep-linkable like
 * Conflicts': Part VI, and the board's compensation decisions (CBG-UX8-MENU; D-CBG-35 (1), Jonathan
 * 2026-10-22 23:09 PDT: "Evaluations" shows as "Self-assessment", and its Compensation decisions move
 * to the Form 990 page as its second part, beside line 15, which lists each year's). An address with
 * no part opens Part VI, so every earlier link to the Form 990 page lands where it did.
 */
export const FORM_990_TABS = {
  partVi: 'part-vi',
  compensation: 'compensation',
} as const;
export type Form990Tab = (typeof FORM_990_TABS)[keyof typeof FORM_990_TABS];

/**
 * Where the compensation decisions were before they moved: the `?tab=` word of their part on the page
 * that now shows as Self-assessment (CBG-YR4-PAY, `/evaluations?tab=compensation`). A host replaces
 * that address with the Form 990 page's part, so a link someone kept still opens the decisions.
 */
export const RETIRED_COMPENSATION_TAB = 'compensation';

export function isGovernanceTab(value: unknown): value is GovernanceTab {
  return (
    value === GOVERNANCE_TABS.documents ||
    value === GOVERNANCE_TABS.meetingRules ||
    value === GOVERNANCE_TABS.terms ||
    value === GOVERNANCE_TABS.officers
  );
}
